import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "env";
import {
    FB_FINDER_EXT_SOURCE,
    FB_FINDER_PAGE_SOURCE,
    FbFinderUpdate,
} from "../utils/facebook";

/**
 * Cắt spinner nếu extension chết giữa chừng, tránh nút quay mãi không dừng.
 * Để rộng vì ca trùng tên phải mở lần lượt từng người để đối chiếu tin nhắn cuối:
 * hộp thư load ~20s, mỗi lượt đối chiếu ~15s, tối đa 3 người.
 */
const FIND_TIMEOUT_MS = 150_000;

export type FbFinderResult = { customerId: number; ok: boolean; reason: string };

/**
 * Cầu nối phía React với extension Khothi FB Finder.
 *
 * - `installed`: có extension hay không (dùng để bật/tắt nút)
 * - `findingId`: khách đang được dò -> nút hiện trạng thái quay
 * - `startFind`: báo extension bắt đầu; extension tự mở tab NGẦM, nhân viên ở nguyên CMS
 * - `onUpdated`: đã ghi xong profile_link (và ảnh nếu có)
 * - `onResult`: kết thúc một lượt, kể cả thất bại -> tắt spinner, báo nhân viên
 *
 * Trang chỉ nói chuyện với extension qua window.postMessage cùng origin.
 * Token đăng nhập không đi qua đây; extension tự đọc tại origin CMS khi gọi API.
 */
export function useFbFinder(
    onUpdated?: (u: FbFinderUpdate) => void,
    onResult?: (r: FbFinderResult) => void
) {
    const [installed, setInstalled] = useState(false);
    const [findingId, setFindingId] = useState<number | null>(null);

    const updatedRef = useRef(onUpdated);
    updatedRef.current = onUpdated;
    const resultRef = useRef(onResult);
    resultRef.current = onResult;
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const stopFinding = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        setFindingId(null);
    }, []);

    useEffect(() => {
        const onMessage = (ev: MessageEvent) => {
            if (ev.source !== window) return;
            const d: any = ev.data;
            if (!d || d.source !== FB_FINDER_EXT_SOURCE) return;

            if (d.type === "READY" || d.type === "FIND_ARMED") {
                setInstalled(true);
                return;
            }

            if (d.type === "PROFILE_UPDATED") {
                setInstalled(true);
                updatedRef.current?.({
                    customerId: Number(d.customerId),
                    profileLink: String(d.profileLink || ""),
                    fbUid: d.fbUid ? String(d.fbUid) : null,
                    avatarUploaded: !!d.avatarUploaded,
                });
                return;
            }

            if (d.type === "FIND_DONE") {
                stopFinding();
                resultRef.current?.({
                    customerId: Number(d.customerId),
                    ok: !!d.ok,
                    reason: String(d.reason || ""),
                });
            }
        };

        window.addEventListener("message", onMessage);
        // Extension có thể đã nạp xong trước khi component này mount -> hỏi lại.
        window.postMessage({ source: FB_FINDER_PAGE_SOURCE, type: "PING" }, window.location.origin);

        return () => {
            window.removeEventListener("message", onMessage);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [stopFinding]);

    const startFind = useCallback(
        (params: {
            customerId: number;
            customerName?: string;
            targetUrl?: string;
            lastMessage?: string;
        }) => {
            setFindingId(params.customerId);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                setFindingId(null);
                resultRef.current?.({ customerId: params.customerId, ok: false, reason: "TIMEOUT" });
            }, FIND_TIMEOUT_MS);

            window.postMessage(
                {
                    source: FB_FINDER_PAGE_SOURCE,
                    type: "FB_FIND_START",
                    customerId: params.customerId,
                    customerName: params.customerName || "",
                    // Dùng để khớp đúng hội thoại trong hộp thư Business Suite
                    lastMessage: params.lastMessage || "",
                    // Extension mở tab này ở chế độ nền; trang CMS không tự mở tab nữa
                    // (vừa để nhân viên không bị nhảy màn hình, vừa hết bị chặn popup).
                    targetUrl: params.targetUrl || "https://www.facebook.com/",
                    apiBase: API_URL,
                },
                window.location.origin
            );
        },
        []
    );

    return { installed, findingId, startFind, stopFinding };
}
